import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { ProgramCategory, SessionCountByProgram, SessionLimitByProgram } from '../../../models/session/SessionUsage'
import { FOURTEEN_DAYS_TO_MS, SIXTY_DAYS_TO_MS, THIRTY_DAYS_TO_MS } from '../../calendar/types'
import { isBCMProvider, isCLEProvider, isNeurodiversityProvider, isPerformanceProvider } from '../../providers/utils'
import { CoachingSessionTypes, NeurodiversitySessionTypes } from '../../sessions/constants'
import {
  TREATMENT_OPTIONS_FROM_FRONT_END,
  TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND,
  TREATMENT_OPTIONS_TYPE,
} from '../constants/treatmentOptions'

const coachingSessionTypeValues: Set<string> = new Set(Object.values(CoachingSessionTypes))
const neurodiversitySessionTypeValues: Set<string> = new Set(Object.values(NeurodiversitySessionTypes))

export enum SESSIONS_COVERAGE_TITLE_ID {
  SESSIONS_TITLE = 'sessionsTitle',
  TOTAL_SESSIONS_TITLE = 'totalSessionsTitle',
  THERAPY_SESSIONS_TITLE = 'therapySessionsTitle',
  COACHING_SESSIONS_TITLE = 'coachingSessionsTitle',
  UNLIMITED_SESSIONS_TITLE = 'unlimitedSessionsTitle',
}

export const getProviderAvailabilityFetchDuration = (programTaxonomy?: ProviderProgramTaxonomy[]): number => {
  const taxonomy = programTaxonomy ?? []

  // BCM and CLE providers get 60 days
  if (isBCMProvider({ programTaxonomy: taxonomy }) || isCLEProvider({ programTaxonomy: taxonomy })) {
    return SIXTY_DAYS_TO_MS
  }

  // Neurodiversity providers get 30 days
  if (isNeurodiversityProvider({ programTaxonomy: taxonomy })) {
    return THIRTY_DAYS_TO_MS
  }

  // Performance coaching providers get 30 days
  if (isPerformanceProvider({ programTaxonomy: taxonomy })) {
    return THIRTY_DAYS_TO_MS
  }

  // All other providers get 14 days
  return FOURTEEN_DAYS_TO_MS
}

export function getSessionLimitProgramCategoryByTreatmentOption(
  treatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramCategory {
  switch (treatmentOption) {
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.LIVE_MESSAGING_COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.GUIDED_SELF_CARE:
      return 'coaching'
    case TREATMENT_OPTIONS_FROM_FRONT_END.CAREGIVER_TRAINING_FOR_NEURO:
    case TREATMENT_OPTIONS_FROM_FRONT_END.NEURODIVERSITY_SKILLS_COACHING:
      return 'neurodiversity'
    default:
      // all other treatment options are considered therapy
      return 'therapy'
  }
}

export function getSessionLimitProgramCategoryBySessionType(sessionType: string): ProgramCategory {
  if (neurodiversitySessionTypeValues.has(sessionType)) return 'neurodiversity'
  if (coachingSessionTypeValues.has(sessionType)) return 'coaching'
  return 'therapy'
}

export const createRemainingSessionCountByProgram = (
  EAPSessionLimitByProgram?: SessionLimitByProgram,
  usedEAPSessionCountByProgram?: SessionCountByProgram,
): SessionCountByProgram => {
  if (!EAPSessionLimitByProgram) {
    return {}
  }
  return Object.keys(EAPSessionLimitByProgram).reduce(
    (sessionsRemainingByProgram: SessionCountByProgram, program: string) => {
      const programCategory = program as ProgramCategory
      const remainingSessionCount =
        (EAPSessionLimitByProgram[programCategory] ?? 0) - (usedEAPSessionCountByProgram?.[programCategory] ?? 0)
      sessionsRemainingByProgram[programCategory] = Math.max(remainingSessionCount, 0)
      return sessionsRemainingByProgram
    },
    {} as SessionCountByProgram,
  )
}

/**
 * Returns the number of EAP sessions available for any program not explicitly listed
 * in `numberOfSessionsByProgram` (e.g coaching & therapy when `numberOfSessionsByProgram`: {'neurodiversity': 8}).
 * Per design, this remainder is rendered as an unlabeled row in the "Total sessions" card
 * (no "other"/category label) when a customer has a per-program limit (e.g. neurodiversity) but
 * does not split therapy/coaching explicitly.
 * Unlimited limits (`999`) are excluded from the sum so they do not eat the remainder.
 * Unknown program keys are ignored so a stray backend key cannot silently shrink the remainder
 * pool — matches the filtering in `computeRemainderUsed`.
 */
export const computeRemainderSessions = (
  numberOfSessions?: number,
  sessionLimitByProgram?: SessionLimitByProgram | null,
): number => {
  if (numberOfSessions === undefined) {
    return 0
  }
  if (!sessionLimitByProgram) {
    return numberOfSessions
  }
  const explicitTotal = Object.keys(sessionLimitByProgram)
    .filter((key): key is ProgramCategory => isKnownProgramCategory(key))
    .reduce<number>((sum, programCategory) => {
      const limit = sessionLimitByProgram[programCategory]
      if (limit === undefined || isUnlimitedSessionLimit(limit)) {
        return sum
      }
      return sum + limit
    }, 0)
  return Math.max(numberOfSessions - explicitTotal, 0)
}

/**
 * Returns the number of EAP sessions used against the shared remainder pool —
 * i.e. total `sessionsUsed` minus usage attributed to programs that have an explicit
 * limit in `sessionLimitByProgram`. Mirrors `computeRemainderSessions` on the used side.
 *
 * Unknown program keys in `sessionLimitByProgram` are ignored so a stray backend key
 * cannot inflate `explicitUsed` and silently undercount the remainder.
 */
export const computeRemainderUsed = (
  sessionsUsed?: number,
  sessionLimitByProgram?: SessionLimitByProgram,
  usedSessionsByProgram?: SessionCountByProgram,
): number => {
  if (sessionsUsed === undefined) {
    return 0
  }
  const explicitUsed = Object.keys(sessionLimitByProgram ?? {})
    .filter((key): key is ProgramCategory => isKnownProgramCategory(key))
    .reduce<number>((sum, programCategory) => sum + (usedSessionsByProgram?.[programCategory] ?? 0), 0)
  return Math.max(sessionsUsed - explicitUsed, 0)
}

export const getSpecializedSessionsCount = (sessionLimitByProgram?: SessionLimitByProgram): number | undefined => {
  return Object.keys(sessionLimitByProgram ?? {})
    .filter((key): key is ProgramCategory => isSpecializedProgramCategory(key as ProgramCategory))
    .reduce<number>((sum, programCategory) => sum + (sessionLimitByProgram?.[programCategory] ?? 0), 0)
}

/** `displaySessions` to be used with the `messageId` to display the correct sessions count message */
export type SessionCountForDisplay = {
  therapySessions: string | number | undefined
  coachingSessions: string | number | undefined
  neurodiversitySessions: string | number | undefined
  displaySessions: string | number | undefined
  messageId: SESSIONS_COVERAGE_TITLE_ID
}

export const getSessionCountForDisplay = ({
  numSessions,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
}: {
  numSessions?: string
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}): SessionCountForDisplay => {
  const result: SessionCountForDisplay = {
    therapySessions: numSessions,
    coachingSessions: numSessions,
    neurodiversitySessions: numberOfSessionsByProgram?.neurodiversity,
    displaySessions: numSessions,
    messageId: SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE,
  }
  if (numSessions !== undefined && isUnlimitedSessionLimit(Number(numSessions))) {
    result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
    result.displaySessions = undefined
  }

  if (
    displayVisitsPerIssuePerYearByProgram ||
    (numberOfSessionsByProgram?.coaching && numberOfSessionsByProgram?.therapy)
  ) {
    let therapy = numberOfSessionsByProgram?.therapy
    let coaching = numberOfSessionsByProgram?.coaching
    if (displayVisitsPerIssuePerYearByProgram) {
      therapy = displayVisitsPerIssuePerYearByProgram.therapy
      coaching = displayVisitsPerIssuePerYearByProgram.coaching
    }

    result.coachingSessions = coaching
    result.therapySessions = therapy
    result.displaySessions = (therapy || 0) + (coaching || 0)
    result.messageId = displayVisitsPerIssuePerYearByProgram
      ? SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE
      : SESSIONS_COVERAGE_TITLE_ID.TOTAL_SESSIONS_TITLE

    if (
      isUnlimitedSessionLimit(Number(result.coachingSessions)) &&
      isUnlimitedSessionLimit(Number(result.therapySessions))
    ) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
      result.displaySessions = undefined
    } else if (isUnlimitedSessionLimit(Number(result.coachingSessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.THERAPY_SESSIONS_TITLE
      result.displaySessions = result.therapySessions
    } else if (isUnlimitedSessionLimit(Number(result.therapySessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.COACHING_SESSIONS_TITLE
      result.displaySessions = result.coachingSessions
    }
  } else if (displayVisitsPerIssuePerYear) {
    result.coachingSessions = displayVisitsPerIssuePerYear
    result.therapySessions = displayVisitsPerIssuePerYear
    result.displaySessions = displayVisitsPerIssuePerYear
    result.messageId = SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE
    if (isUnlimitedSessionLimit(Number(result.displaySessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
      result.displaySessions = undefined
    }
  } else if (numberOfSessionsByProgram) {
    // Reached only when therapy and coaching are NOT both split out (the first `if` handles
    // that), so today this is the "Neuro Only" scenario: a single shared therapy/coaching pool
    // alongside a dedicated program (e.g. neurodiversity). We badge that shared remainder
    // (total minus program-specific limits) on the combined "Therapy or coaching" row, not the
    // grand total — matching the description text, which already uses `computeRemainderSessions`.
    // NOTE: therapy and coaching are intentionally set to the same shared value here. If a future
    // config splits out only one of them alongside a dedicated program (e.g. { coaching, neurodiversity }),
    // revisit this so the explicit per-program limit isn't collapsed into the shared remainder.
    const remainderSessions = computeRemainderSessions(Number(numSessions), numberOfSessionsByProgram)
    result.therapySessions = remainderSessions
    result.coachingSessions = remainderSessions
    result.displaySessions = remainderSessions
  }

  return result
}

export const isProgramAvailableForMinor = (program: ProgramCategory): boolean => program === 'therapy'

// Listed as `ProgramCategory[]` so TypeScript catches misspellings; the `Set` is `Set<string>` so we
// can membership-test arbitrary backend keys without casting.
const KNOWN_PROGRAM_CATEGORIES_LIST = ['therapy', 'coaching', 'neurodiversity'] satisfies ProgramCategory[]
const KNOWN_PROGRAM_CATEGORIES = new Set<string>(KNOWN_PROGRAM_CATEGORIES_LIST)
export const isKnownProgramCategory = (key: string): key is ProgramCategory => KNOWN_PROGRAM_CATEGORIES.has(key)

const SPECIALIZED_PROGRAM_CATEGORIES = new Set<ProgramCategory>(['neurodiversity'])
export const isSpecializedProgramCategory = (program: ProgramCategory): boolean =>
  SPECIALIZED_PROGRAM_CATEGORIES.has(program)

export const UNLIMITED_SESSION_LIMIT = 999
export const isUnlimitedSessionLimit = (sessionLimit: number | undefined): boolean =>
  sessionLimit === UNLIMITED_SESSION_LIMIT

/**
 * Calculates dual entitlement status and determines which program type (therapy or coaching)
 * has fewer sessions remaining for EAP to HPI transition messaging.
 *
 * @param sessionLimitByProgram - Session limits by program category (therapy/coaching)
 * @param usedSessionCountByProgram - Used session counts by program category
 * @returns Object containing:
 *   - hasDualEntitlementEnabled: true if user has separate limits for therapy and coaching
 *   - isTherapy: true if therapy has fewer remaining sessions than coaching
 */
export const getDualEntitlementStatus = (
  sessionLimitByProgram?: SessionLimitByProgram,
  usedSessionCountByProgram?: SessionCountByProgram,
): {
  hasDualEntitlementEnabled: boolean
  isTherapy: boolean
} => {
  const employeeSessionsRemainingByProgram = createRemainingSessionCountByProgram(
    sessionLimitByProgram,
    usedSessionCountByProgram,
  )

  const hasDualEntitlementEnabled =
    sessionLimitByProgram != null && Object.keys(employeeSessionsRemainingByProgram).length > 1

  const therapySessionLimitRemaining = employeeSessionsRemainingByProgram?.therapy ?? 0
  const coachingSessionLimitRemaining = employeeSessionsRemainingByProgram?.coaching ?? 0
  const isTherapy = therapySessionLimitRemaining < coachingSessionLimitRemaining

  return {
    hasDualEntitlementEnabled,
    isTherapy,
  }
}
